import { AccessLevel } from "@/api/williams/users";
import { faMagnifyingGlass, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getStatusText } from "../lookup/status";
import { NewProjectDialog, SelectProjectDialog } from "../../projects/dialogs";
import { TestEvent, TestEventStatus } from "@/api/williams/testEvents";
import { useContractorLookup } from "../../contractors/hooks";
import { useFormContext } from "react-hook-form";
import { useModalDialog } from "@/hooks";
import { usePermissions, useWeldingSpecialists } from "../../users/hooks";

export type TestEventFormProps = {
  data: TestEvent;
};

export function TEForm({ data }: Readonly<TestEventFormProps>) {
  const form = useFormContext<TestEvent>();
  const modal = useModalDialog();
  const specialists = useWeldingSpecialists();
  const contractors = useContractorLookup();
  const permissions = usePermissions();

  const errors = form.formState.errors;
  const isLocked = data.locked || permissions.data?.accessLevel === AccessLevel.GeneralUser;

  function handleSelectProject(createNew: boolean) {
    const dialog = createNew ? NewProjectDialog : SelectProjectDialog;

    modal.show(dialog, {
      initialProject: data.project,
      onAccept: (project) => {
        form.clearErrors("project");
        form.setValue("project", project, { shouldDirty: true });
      },
    });
  }

  return (
    <form>
      <div className="row">
        {/* First Column */}
        <div className="col-lg-6 pe-md-3 pe-xl-5">
          {/* Event number */}
          <div>
            <label htmlFor="number" className="col-form-label pb-0">
              Event Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.number && "border-danger"}`}
              readOnly={true}
              tabIndex={-1}
              disabled={isLocked}
              {...form.register("number")}
            />
            {errors.number && <span className="text-danger">{errors.number.message}</span>}
          </div>
          {/* Description */}
          <div>
            <label htmlFor="number" className="col-form-label pb-0">
              Description <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              maxLength={50}
              className={`form-control ${errors.description && "border-danger"}`}
              autoComplete="off"
              disabled={isLocked}
              {...form.register("description")}
            />
            {errors.description && <span className="text-danger">{errors.description.message}</span>}
          </div>
          {/* Project Id */}
          <div>
            {/* Required for validation to work properly */}
            <input type="hidden" {...form.register("project.id")} />
          </div>
          {/* Project Number */}
          <div>
            <label htmlFor="projectNumber" className="col-form-label pb-0">
              Project Number <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <input
                type="text"
                className={`form-control ${errors.project && "border-danger"}`}
                readOnly
                tabIndex={-1}
                disabled={isLocked}
                {...form.register("project.number")}
              />
              {!isLocked && (
                <span className="input-group-text" role="button" onClick={() => handleSelectProject(false)}>
                  <FontAwesomeIcon size="sm" icon={faMagnifyingGlass} title="Search" />
                </span>
              )}
              {!isLocked && (
                <span className="input-group-text" role="button" onClick={() => handleSelectProject(true)}>
                  <FontAwesomeIcon size="sm" icon={faPlus} title="New Project" />
                </span>
              )}
            </div>
            {errors.project?.number && <span className="text-danger">{errors.project.number.message}</span>}
          </div>
          {/* Project Name */}
          <div>
            <label htmlFor="projectName" className="col-form-label pb-0">
              Project Name
            </label>
            <input
              type="text"
              className="form-control"
              readOnly
              tabIndex={-1}
              disabled={isLocked}
              {...form.register("project.name")}
            />
          </div>
          {/* Project Description */}
          <div>
            <label htmlFor="projectDescription" className="col-form-label pb-0">
              Project Description
            </label>
            <input
              type="text"
              className="form-control"
              readOnly
              tabIndex={-1}
              disabled={isLocked}
              {...form.register("project.description")}
            />
          </div>
          {/* Owner Id */}
          <div>
            {/* Required for validation to work properly */}
            <input type="hidden" {...form.register("owner")} />
          </div>
          {/* Owner */}
          <div>
            <label htmlFor="owner" className="col-form-label pb-0">
              Owner <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              value={data.owner?.name ?? data.owner?.userName}
              readOnly
              tabIndex={-1}
              disabled={isLocked}
            />
          </div>
        </div>

        {/* Second Column */}
        <div className="col-lg-6 ps-md-3 ps-xl-5">
          {/* Contractor */}
          <div>
            <label htmlFor="contractorId" className="col-form-label pb-0">
              Contractor {data.status > TestEventStatus.Partial && <span className="text-danger">*</span>}
            </label>
            <select
              className={`form-select ${errors.contractorId && "border-danger"}`}
              disabled={isLocked || data.tests.length > 0}
              {...form.register("contractorId")}
            >
              <option />
              {contractors.data?.results &&
                contractors.data.results.map((contractor) => (
                  <option key={contractor.id} value={contractor.id}>
                    {contractor.name}
                  </option>
                ))}
            </select>
            {errors.contractorId && <span className="text-danger">{errors.contractorId.message}</span>}
          </div>
          {/* Contractor supervisor */}
          <div>
            <label htmlFor="supervisor" className="col-form-label pb-0">
              Contractor Supervisor {data.status > TestEventStatus.Partial && <span className="text-danger">*</span>}
            </label>
            <input
              type="text"
              maxLength={50}
              className={`form-control ${errors.supervisor && "border-danger"}`}
              disabled={isLocked}
              {...form.register("supervisor")}
            />
            {errors.supervisor && <span className="text-danger">{errors.supervisor.message}</span>}
          </div>
          {/* Welding/NDE Specialist */}
          <div>
            <label htmlFor="weldingSpecialistId" className="col-form-label pb-0">
              Welding/NDE Specialist <span className="text-danger">*</span>
            </label>
            <select
              className={`form-select ${errors.weldingSpecialistId && "border-danger"}`}
              disabled={isLocked}
              {...form.register("weldingSpecialistId")}
            >
              <option />
              {specialists.data &&
                specialists.data.map((specialist) => (
                  <option key={specialist.id} value={specialist.id}>
                    {specialist.name ?? specialist.userName}
                  </option>
                ))}
            </select>
            {errors.weldingSpecialistId && <span className="text-danger">{errors.weldingSpecialistId.message}</span>}
          </div>
          {/* Test Event Date */}
          <div>
            <label htmlFor="testEventDate" className="col-form-label pb-0">
              Test Event Date
            </label>
            <input
              type="date"
              min="1753-01-01"
              className={`form-control ${errors.testEventDate && "border-danger"}`}
              disabled={isLocked}
              {...form.register("testEventDate")}
            />
            {errors.testEventDate && <span className="text-danger">{errors.testEventDate.message}</span>}
          </div>
          {/* Status */}
          <div>
            <label htmlFor="status" className="col-form-label pb-0">
              Status
            </label>
            <input
              type="text"
              className="form-control"
              value={getStatusText(data.status)}
              readOnly
              tabIndex={-1}
              disabled={isLocked}
            />
          </div>
        </div>
      </div>
    </form>
  );
}
